import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionManagement } from "@/hooks/useSessionManagement";
import { useStudentAttendance } from "@/hooks/useStudentAttendance";
import { useCurrencySettings } from "@/hooks/useCurrencySettings";
import { Badge } from "@/components/ui/badge";
import { Trophy, MessageCircle, Bot } from "lucide-react";
import StudentBeltRequirements from "@/components/StudentBeltRequirements";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentDojo } from '../hooks/useCurrentDojo';
import { usePremiumAccess } from '../hooks/usePremiumAccess';
import { useTrialStatus } from '../hooks/useTrialStatus';


import UpcomingSessionsCard from "@/components/dashboard/UpcomingSessionsCard";
import BeltTestsCard from "@/components/dashboard/BeltTestsCard";
import WeeklyTrainingPlan from "@/components/dashboard/WeeklyTrainingPlan";
import OnDemandTrainingTab from "@/components/dashboard/OnDemandTrainingTab";
import StudentPaymentTracker from "@/components/StudentPaymentTracker";
import StudentProfileManager from "@/components/StudentProfileManager";
import StudentNotifications from "@/components/StudentNotifications";
import WarriorAI from "@/components/students/WarriorAI";
import Layout from "../components/Layout";
import StudentDashboardHeader from '../components/dashboard/StudentDashboardHeader';

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
  requirements: Requirement[];
}

interface Requirement {
  id: string;
  title: string;
  description: string;
  category: string;
  points?: number;
  difficulty_level?: string;
  is_required?: boolean;
}

interface StudentProgress {
  requirement_id: string;
  completed: boolean;
  completed_date?: string;
}

const StudentDashboard = () => {
  useUiLanguage();
  const { user, profile } = useAuth();
  const { dojo } = useCurrentDojo();
  const { hasPremiumAccess } = usePremiumAccess();
  const { isAccountLocked } = useTrialStatus();

  // Redirect to trial expired if account is locked
  if (isAccountLocked) {
    return null; // The main App component will handle showing TrialExpiredScreen
  }
  const { t } = useTranslation();
  const [upcomingPromotions, setUpcomingPromotions] = useState<any[]>([]);
  const [currentBelt, setCurrentBelt] = useState<string>("White");
  const [currentBeltLevel, setCurrentBeltLevel] = useState<BeltLevel | null>(
    null,
  );
  const [nextBeltLevel, setNextBeltLevel] = useState<BeltLevel | null>(null);
  const [completedRequirements, setCompletedRequirements] = useState<string[]>(
    [],
  );
  const [studentProgress, setStudentProgress] = useState<StudentProgress[]>([]);
  const [isLoadingBelt, setIsLoadingBelt] = useState(false);
  const [showPromotionRequirements, setShowPromotionRequirements] =
    useState(true);
  const { attendanceData } = useStudentAttendance();
  const [activeTab, setActiveTab] = useState("dashboard");

  const {
    sessions,
    promotionSessions,
    isLoading: sessionsLoading,
    confirmPromotionAttendance,
  } = useSessionManagement();

  const displayName = profile
    ? `${profile.first_name} ${profile.last_name}`
    : user?.email || "Student";

  const formatDateToDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Real-time sync with syllabus changes - using student's own dojo_id
  useEffect(() => {
    if (!profile?.dojo_id) return;

    const handleBeltSystemChange = () => {
      console.log(
        "🔄 Student dashboard: Belt system changed - full refresh...",
        "Student dojo_id:", profile?.dojo_id
      );
      setTimeout(() => {
        fetchCurrentBelt();
        fetchPromotionRequirements();
        fetchStudentProgress();
      }, 500);
    };

    window.addEventListener("beltSystemChanged", handleBeltSystemChange);

    return () => {
      window.removeEventListener("beltSystemChanged", handleBeltSystemChange);
    };
  }, [profile?.belt_level_id, profile?.dojo_id]);

  useEffect(() => {
    if (profile) {
      fetchCurrentBelt();
      fetchPromotionRequirements();
      fetchStudentProgress();

      // Mark previous belt requirements as completed when component loads
      if (profile.belt_level_id) {
        markPreviousBeltRequirementsCompleted();
      }
    }
  }, [profile]);

  // Watch for belt level changes and auto-complete previous requirements
  useEffect(() => {
    if (profile?.belt_level_id && currentBeltLevel) {
      markPreviousBeltRequirementsCompleted();
    }
  }, [profile?.belt_level_id]);

  // Optimized real-time subscriptions
  useEffect(() => {
    if (!profile?.id || !profile?.dojo_id) return;

    let subscriptions: any[] = [];

    // Set up subscriptions and store them for cleanup
    const setupSubscriptions = async () => {
      try {
        // Student progress subscription
        const progressSub = supabase
          .channel(`student_progress_${profile.id}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'student_progress',
            filter: `student_id=eq.${profile.id}`
          }, (payload) => {
            console.log('📈 Student progress changed:', payload);
            fetchStudentProgress();
          })
          .subscribe();

        subscriptions.push(progressSub);

        // Notifications subscription
        const notificationsSub = supabase
          .channel(`notifications_${profile.id}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${profile.id}`
          }, (payload) => {
            console.log('🔔 Notifications changed:', payload);
            // Trigger notification refresh if needed
          })
          .subscribe();

        subscriptions.push(notificationsSub);

      } catch (error) {
        console.error('Error setting up subscriptions:', error);
      }
    };

    setupSubscriptions();

    return () => {
      // Clean up all subscriptions
      subscriptions.forEach(sub => {
        if (sub && typeof sub.unsubscribe === 'function') {
          sub.unsubscribe();
        }
      });
      subscriptions = [];
    };
  }, [profile?.id, profile?.dojo_id]);

  const fetchUpcomingPromotions = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from("promotion_sessions")
        .select("*")
        .gte("date", new Date().toISOString().split("T")[0])
        .order("date", { ascending: true })
        .limit(3);

      if (error) throw error;
      setUpcomingPromotions(data || []);
    } catch (error) {
      console.error("Error fetching upcoming promotions:", error);
    }
  };

  const fetchCurrentBelt = async () => {
    if (!profile?.belt_level_id) {
      setCurrentBelt("No Belt Assigned");
      setCurrentBeltLevel(null);
      return;
    }

    setIsLoadingBelt(true);
    try {
      // Single optimized query
      const { data: beltData, error: beltError } = await supabase
        .from("belt_levels")
        .select("id, name, color, order_sequence, requirements")
        .eq("id", profile.belt_level_id)
        .single();

      if (beltError) {
        setCurrentBelt("No Belt Assigned");
        setCurrentBeltLevel(null);
        return;
      }

      // Process requirements efficiently
      const requirements: Requirement[] = Array.isArray(beltData.requirements)
        ? beltData.requirements.map((req, index) => ({
            id: req.id || `req_${index}`,
            title: req.title,
            description: req.description || "",
            category: req.category || "General",
            points: req.points || 1,
            difficulty_level: req.difficulty_level || "beginner",
            is_required: req.is_required !== false,
          }))
        : [];

      setCurrentBelt(beltData.name);
      setCurrentBeltLevel({ ...beltData, requirements });
    } catch (error) {
      console.error("Error fetching current belt:", error);
      setCurrentBelt("No Belt Assigned");
      setCurrentBeltLevel(null);
    } finally {
      setIsLoadingBelt(false);
    }
  };

  const fetchPromotionRequirements = async () => {
    if (!profile?.dojo_id) return;

    try {
      if (!profile?.belt_level_id) {
        // Student has no belt - show first belt requirements
        console.log("🆕 No current belt, fetching first belt...");
        const { data: firstBelt, error } = await supabase
          .from("belt_levels")
          .select("*")
          .eq("dojo_id", profile.dojo_id)
          .order("order_sequence", { ascending: true })
          .limit(1)
          .single();

        if (error) {
          console.error("Error fetching first belt:", error);
          return;
        }

        // Get requirements directly from admin syllabus (JSONB field)
        let requirements: Requirement[] = [];

        if (Array.isArray(firstBelt.requirements) && firstBelt.requirements.length > 0) {
          requirements = firstBelt.requirements.map((req, index) => ({
            id: req.id || `req_${index}_${Date.now()}`,
            title: req.title,
            description: req.description || "",
            category: req.category || "General",
            points: req.points || 1,
            difficulty_level: req.difficulty_level || "beginner",
            is_required: req.is_required !== false,
          }));
          console.log("✅ Loaded", requirements.length, "requirements from admin syllabus for first belt:", firstBelt.name);
        } else {
          requirements = [];
          console.log("⚠️ No requirements configured in admin syllabus for first belt:", firstBelt.name);
        }

        setNextBeltLevel({
          ...firstBelt,
          requirements,
        });

        console.log("✅ First belt loaded for new student:", firstBelt.name);
        return;
      }

      // Get current belt to find next one
      const { data: currentBelt, error: currentError } = await supabase
        .from("belt_levels")
        .select("*")
        .eq("id", profile.belt_level_id)
        .single();

      if (currentError) {
        console.error("Error fetching current belt for promotion:", currentError);
        return;
      }

      console.log("🎯 Finding next belt after:", currentBelt.name, "order:", currentBelt.order_sequence);

      // Get next belt level (current order + 1)
      const { data: nextBeltLevel, error: nextBeltError } = await supabase
        .from('belt_levels')
        .select('*')
        .eq('dojo_id', profile.dojo_id)
        .eq('order_sequence', currentBelt.order_sequence + 1)
        .single();

      if (nextBeltError || !nextBeltLevel) {
        console.log('⚠️ No next belt found for promotion. Current belt order:', currentBelt.order_sequence);
        setNextBeltLevel(null);
        return;
      }

      // Get requirements directly from admin syllabus (JSONB field)
      let requirements: Requirement[] = [];

      if (Array.isArray(nextBeltLevel.requirements) && nextBeltLevel.requirements.length > 0) {
        requirements = nextBeltLevel.requirements.map((req, index) => ({
          id: req.id || `req_${index}_${Date.now()}`,
          title: req.title,
          description: req.description || "",
          category: req.category || "General",
          points: req.points || 1,
          difficulty_level: req.difficulty_level || "beginner",
          is_required: req.is_required !== false,
        }));
        console.log("✅ Loaded", requirements.length, "requirements from admin syllabus for next belt:", nextBeltLevel.name);
      } else {
        requirements = [];
        console.log("⚠️ No requirements configured in admin syllabus for next belt:", nextBeltLevel.name);
      }

      setNextBeltLevel({
        ...nextBeltLevel,
        requirements,
      });

      setNextBeltLevel({
        ...nextBeltLevel,
        requirements,
      });

      console.log("🎯 Next belt for promotion:", nextBeltLevel.name, "with", requirements.length, "requirements from admin syllabus");
    } catch (error) {
      console.error("Error fetching promotion requirements:", error);
      setNextBeltLevel(null);
    }
  };

  const fetchStudentProgress = async () => {
    if (!profile?.id) return;

    try {
      console.log("📊 Fetching student progress for:", profile.id);

      // Try new student_requirements table first
      const { data: newProgress, error: newError } = await supabase
        .from("student_requirements")
        .select("*")
        .eq("student_id", profile.id);

      if (!newError && newProgress) {
        // Map new structure to old format
        const mappedProgress = newProgress.map((item) => ({
          requirement_id: item.belt_requirement_id || item.requirement_name,
          completed: item.completed,
          completed_date: item.completed_date,
        }));
        setStudentProgress(mappedProgress);

        const completed = mappedProgress
          .filter((progress) => progress.completed)
          .map((progress) => progress.requirement_id);

        setCompletedRequirements(completed);
        console.log(
          "📈 Student progress loaded (new format):",
          completed.length,
          "completed requirements",
        );
        return;
      }

      // Fallback to old student_progress table
      const { data: oldProgress, error: oldError } = await supabase
        .from("student_progress")
        .select("*")
        .eq("student_id", profile.id);

      if (oldError) {
        console.error("Error fetching student progress:", oldError);
        return;
      }

      setStudentProgress(oldProgress || []);

      const completed = (oldProgress || [])
        .filter((progress) => progress.completed)
        .map((progress) => progress.requirement_id);

      setCompletedRequirements(completed);
      console.log(
        "📈 Student progress loaded (old format):",
        completed.length,
        "completed requirements",
      );
    } catch (error) {
      console.error("Error fetching student progress:", error);
    }
  };

  const markPreviousBeltRequirementsCompleted = async () => {
    if (!profile?.id || !profile?.belt_level_id || !profile?.dojo_id) {
      console.log("❌ Cannot mark requirements - missing profile data");
      return;
    }

    try {
      console.log(
        "🚀 ADMIN BELT CHANGE DETECTED! Starting auto-completion for belt:",
        profile.belt_level_id,
      );

      const { data: currentBelt, error: currentError } = await supabase
        .from("belt_levels")
        .select("*")
        .eq("id", profile.belt_level_id)
        .single();

      if (currentError) {
        console.error("Error fetching current belt:", currentError);
        return;
      }

      console.log(
        "📊 Current belt:",
        currentBelt.name,
        "order:",
        currentBelt.order_sequence,
      );

      // Only auto-complete if current belt order is greater than 0 (not the first belt)
      if (currentBelt.order_sequence <= 0) {
        console.log(
          "📌 Student is at first belt, no previous belts to auto-complete",
        );
        return;
      }

      const { data: previousBelts, error: previousError } = await supabase
        .from("belt_levels")
        .select("*")
        .eq("dojo_id", profile.dojo_id)
        .lt("order_sequence", currentBelt.order_sequence)
        .order("order_sequence", { ascending: true });

      if (previousError) {
        console.error("Error fetching previous belts:", previousError);
        return;
      }

      console.log(
        "🎯 Found",
        previousBelts.length,
        "previous belts to auto-complete",
      );

      let totalMarked = 0;

      // Mark requirements from previous belts as completed
      for (const belt of previousBelts) {
        console.log(
          "⚡ Auto-completing belt:",
          belt.name,
          "order:",
          belt.order_sequence,
        );

        // Get requirements from new table structure first
        const { data: beltRequirements, error: reqError } = await supabase
          .from("belt_requirements")
          .select("*")
          .eq("belt_level_id", belt.id);

        let requirements = [];

        if (!reqError && beltRequirements && beltRequirements.length > 0) {
          // Use new relational requirements
          requirements = beltRequirements;
          console.log(
            "📋 Found",
            requirements.length,
            "requirements in belt_requirements table for",
            belt.name,
          );

          for (const requirement of requirements) {
            console.log("✅ Auto-completing requirement:", requirement.title);

            const { error: upsertError } = await supabase
              .from("student_requirements")
              .upsert(
                {
                  student_id: profile.id,
                  belt_level_id: belt.id,
                  belt_requirement_id: requirement.id,
                  requirement_name: requirement.title,
                  completed: true,
                  completed_date: new Date().toISOString(),
                  points_earned: requirement.points || 1,
                  progress_percentage: 100,
                },
                {
                  onConflict: "student_id,belt_requirement_id",
                },
              );

            if (!upsertError) {
              totalMarked++;
            } else {
              console.error(
                "Error marking requirement as completed:",
                upsertError,
              );
            }
          }
        } else if (belt.requirements && Array.isArray(belt.requirements)) {
          // Fallback to JSONB requirements
          requirements = belt.requirements;
          console.log(
            "📋 Using JSONB requirements for",
            belt.name,
            "- found",
            requirements.length,
            "requirements",
          );

          for (const requirement of requirements) {
            console.log(
              "✅ Auto-completing JSONB requirement:",
              requirement.title || requirement.name,
            );

            const { error: upsertError } = await supabase
              .from("student_requirements")
              .upsert(
                {
                  student_id: profile.id,
                  belt_level_id: belt.id,
                  requirement_name: requirement.title || requirement.name,
                  completed: true,
                  completed_date: new Date().toISOString(),
                  points_earned: requirement.points || 1,
                  progress_percentage: 100,
                },
                {
                  onConflict: "student_id,belt_level_id,requirement_name",
                },
              );

            if (!upsertError) {
              totalMarked++;
            } else {
              console.error(
                "Error marking JSONB requirement as completed:",
                upsertError,
              );
            }
          }
        }
      }

      console.log(
        "🎉 ADMIN BELT CHANGE COMPLETE! Successfully auto-completed",
        totalMarked,
        "previous belt requirements",
      );

      // Refresh progress after auto-completion
      setTimeout(() => {
        console.log("🔄 First refresh after auto-completion");
        fetchStudentProgress();
      }, 500);

      setTimeout(() => {
        console.log("🔄 Second refresh after auto-completion");
        fetchStudentProgress();
      }, 1000);
    } catch (error) {
      console.error("Error in auto-completion:", error);
    }
  };

  const toggleRequirementCompletion = async (requirementId: string) => {
    if (!profile?.id) return;

    const isCompleted = completedRequirements.includes(requirementId);

    try {
      if (isCompleted) {
        // Remove from completed - try both tables
        await supabase
          .from("student_requirements")
          .delete()
          .eq("student_id", profile.id)
          .eq("belt_requirement_id", requirementId);

        await supabase
          .from("student_progress")
          .delete()
          .eq("student_id", profile.id)
          .eq("requirement_id", requirementId);

        setCompletedRequirements((prev) =>
          prev.filter((id) => id !== requirementId),
        );
        console.log("❌ Requirement unmarked:", requirementId);
      } else {
        // Add to completed - find the requirement details
        const targetBelt = showPromotionRequirements
          ? nextBeltLevel
          : currentBeltLevel;
        const requirement = targetBelt?.requirements.find(
          (req) => req.id === requirementId,
        );

        if (requirement) {
          // Use new table structure
          await supabase.from("student_requirements").upsert({
            student_id: profile.id,
            belt_level_id: targetBelt.id,
            belt_requirement_id: requirementId,
            requirement_name: requirement.title,
            completed: true,
            completed_date: new Date().toISOString(),
            points_earned: requirement.points || 1,
            progress_percentage: 100,
          });
        }

        // Also update old table for compatibility
        await supabase.from("student_progress").upsert({
          student_id: profile.id,
          requirement_id: requirementId,
          completed: true,
          completed_date: new Date().toISOString(),
        });

        setCompletedRequirements((prev) => [...prev, requirementId]);
        console.log("✅ Requirement completed:", requirementId);
      }
    } catch (error) {
      console.error("Error updating requirement completion:", error);
    }
  };

  const handleConfirmPromotionAttendance = async (
    sessionId: string,
    willAttend: boolean,
  ) => {
    await confirmPromotionAttendance(sessionId, willAttend);
  };

  // Memoize expensive calculations
  const upcomingSessions = useMemo(() => {
    if (!sessions) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return sessions
      .filter((session) => {
        if (!session.date) return false;
        const sessionDate = new Date(session.date);
        const isActive = !session.status || session.status === 'scheduled';
        return sessionDate >= today && isActive;
      })
      .slice(0, 5);
  }, [sessions]);

  console.log('📊 Student Dashboard - Sessions data:', {
    totalSessions: sessions?.length || 0,
    upcomingSessions: upcomingSessions?.length || 0,
    studentDojoId: profile?.dojo_id,
    sessionsLoading,
    allSessions: sessions
  });

  // Calculate progress percentage
  const calculateProgress = () => {
    const targetBelt = showPromotionRequirements
      ? nextBeltLevel
      : currentBeltLevel;
    if (!targetBelt?.requirements || targetBelt.requirements.length === 0) {
      return 0;
    }

    const totalRequirements = targetBelt.requirements.length;
    const completedCount = targetBelt.requirements.filter((req) =>
      completedRequirements.includes(req.id),
    ).length;
    return Math.round((completedCount / totalRequirements) * 100);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="wh-container space-y-6">
          {/* Notifications at the top */}
          <StudentNotifications />

          <StudentDashboardHeader
            displayName={displayName}
            currentBelt={currentBelt}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

        <div className="mt-4 sm:mt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="bg-white dark:bg-gray-800 p-1 sm:p-2 rounded-lg shadow-sm border mb-4 sm:mb-6 overflow-x-auto">
              <TabsList className="tabs-list flex w-full min-w-max bg-gray-50 dark:bg-gray-700 gap-0.5 sm:gap-1 p-1 rounded-md">
                <TabsTrigger
                  value="dashboard"
                  className="tab-trigger data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 hover:bg-blue-600/10 dark:hover:bg-blue-600/20 font-medium rounded-sm"
                >
                  <span className="hidden sm:inline">{t("dashboard:tabs.dashboard")}</span>
                  <span className="sm:hidden">{uiText("Home")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="training"
                  className="tab-trigger data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 hover:bg-blue-600/10 dark:hover:bg-blue-600/20 font-medium rounded-sm"
                >
                  <span className="hidden sm:inline">{t("dashboard:tabs.training")}</span>
                  <span className="sm:hidden">{uiText("Train")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="requirements"
                  className="tab-trigger data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 hover:bg-blue-600/10 dark:hover:bg-blue-600/20 font-medium rounded-sm"
                >
                  <span className="hidden sm:inline">{t("dashboard:tabs.requirements")}</span>
                  <span className="sm:hidden">{uiText("Belts")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="payments"
                  className="tab-trigger data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 hover:bg-blue-600/10 dark:hover:bg-blue-600/20 font-medium rounded-sm"
                >
                  <span className="hidden sm:inline">{t("dashboard:tabs.payments")}</span>
                  <span className="sm:hidden">{uiText("Pay")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="ai-assistant"
                  className="tab-trigger data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 hover:bg-blue-600/10 dark:hover:bg-blue-600/20 font-medium rounded-sm"
                >
                  <span className="hidden sm:inline">{uiText("AI Assistant")}</span>
                  <span className="sm:hidden">{uiText("AI")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="profile"
                  className="tab-trigger data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 hover:bg-blue-600/10 dark:hover:bg-blue-600/20 font-medium rounded-sm"
                >
                  <span className="hidden sm:inline">{t("dashboard:tabs.profile")}</span>
                  <span className="sm:hidden">{uiText("Me")}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="dashboard" className="mt-0">
              <div className="responsive-grid grid-cols-1">
                <div className="responsive-grid grid-cols-1 md:grid-cols-2">
                  <UpcomingSessionsCard
                    upcomingSessions={upcomingSessions || []}
                    userRole={profile?.role}
                  />
                  <BeltTestsCard
                    upcomingPromotions={promotionSessions || []}
                    userRole={profile?.role}
                    onConfirmPromotionAttendance={
                      handleConfirmPromotionAttendance
                    }
                  />
                </div>

                <div className="responsive-grid grid-cols-1">
                  <WeeklyTrainingPlan sessions={upcomingSessions || []} />
                </div>


              </div>
            </TabsContent>

            <TabsContent value="training" className="mt-0">
              <OnDemandTrainingTab />
            </TabsContent>

            <TabsContent value="requirements" className="mt-0">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold">
                    {t("dashboard:sections.beltRequirements.title")}
                  </h2>
                  <Badge variant="outline" className="text-xs">
                    {uiText("Dynamic Requirements from Admin Syllabus")}</Badge>
                </div>

                <StudentBeltRequirements />
              </div>
            </TabsContent>

            <TabsContent value="payments" className="mt-0">
              <StudentPaymentTracker hideInvoices={true} />
            </TabsContent>

            <TabsContent value="ai-assistant" className="mt-0">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                    <Bot className="h-5 w-5" />
                    {uiText("AI Assistant")}</h3>
                  <p className="text-purple-600 mt-2">
                    {uiText("Get personalized martial arts guidance and training advice from your AI instructor.")}</p>
                </div>
                <WarriorAI />
              </div>
            </TabsContent>

            <TabsContent value="profile" className="mt-0">
              <StudentProfileManager />
            </TabsContent>
          </Tabs>
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentDashboard;